// The numbers the Dashboard, Requests, Revenue and Trending tabs computed
// from the downloaded order history, moved verbatim out of
// components/Admin{Overview,Orders,Revenue,Trending}Tab.tsx. Those tabs now
// ask the server (lib/admin/stats.js), which adds the orders up inside
// MongoDB; this module stays as the reference the server must match:
// adminStatsParity.test.ts runs it on the shared fixture and fails unless it
// gets the server's answers. The tabs still use requestDayBounds and the
// Trending range cutoff to build their requests. Change these functions only
// together with the server and its fixture.
import { countsAsRevenue, isUnpaidSelection, commissionOf } from "./orderMoney";
import { TimeRangeKey, partitionByRange } from "./dateRange";
import { User, Order, AdminRequestRow } from "../types/admin";

/* ---------- Dashboard (AdminOverviewTab) ---------- */

export interface OverviewUser {
  _id: string;
  name: string;
  email: string;
  avatarUrl?: string;
}

export interface OverviewOrder {
  _id: string;
  dj: string | { _id: string; name: string; avatarUrl?: string };
  searchQuery: string;
  amount: number;
  createdAt: string;
  status?: "pending" | "queued" | "accepted" | "declined" | "played";
  currency?: string;
  totalAmount?: number;
  djNetShare?: number;
  feeAmount?: number;
  paid?: boolean;
  trackArtists?: string[];
}

// Stable user lookup → the talent behind an order (populated or by id).
export function overviewDjResolver(users: OverviewUser[]) {
  const userMap = new Map<string, OverviewUser>();
  for (const u of users) userMap.set(u._id, u);
  return (djData: OverviewOrder["dj"]) => {
    if (typeof djData === "object" && djData !== null) {
      return { name: djData.name, avatar: djData.avatarUrl, id: djData._id };
    }
    const found = userMap.get(djData);
    return {
      name: found ? found.name : "Unknown",
      avatar: found?.avatarUrl,
      id: djData as string,
    };
  };
}

// Platform-wide KPI stats for the selected window (paid orders only).
// Each currency is totaled on its own and never blended (see ./currency) —
// time filtering and currency splitting compose: we narrow which paid orders
// are summed, then sum each currency separately.
//
// Undated paid orders (no usable date) are handled so money never silently
// disappears: on a bounded window they simply can't be placed in time, so
// they're correctly absent (no note shown). On All Time there's no lower
// bound, so they ARE counted — and we show an informational note saying so.
// `undatedPaidCount` is the real, dynamic count either way.
export function overviewPlatformStats(orders: OverviewOrder[], range: TimeRangeKey, now?: Date) {
  const paid = orders.filter(countsAsRevenue);
  const { inside, undated } = partitionByRange(paid, range, (o) => o.createdAt, now);
  // Per-currency maps — each currency totaled on its own, never blended.
  const platformEarningsByCur: Record<string, number> = {};
  const totalVolumeByCur: Record<string, number> = {};
  const djPayoutsByCur: Record<string, number> = {};
  for (const o of inside) {
    const cur = (o.currency || "usd").toLowerCase();
    const fee = commissionOf(o);
    const vol =
      typeof o.totalAmount === "number" && o.totalAmount > 0 ? o.totalAmount : o.amount / 0.85;
    platformEarningsByCur[cur] = (platformEarningsByCur[cur] || 0) + fee;
    totalVolumeByCur[cur] = (totalVolumeByCur[cur] || 0) + vol;
    // "Talent Payouts" = what talent actually RECEIVES (their net share),
    // not their base price — the base overstated payouts by the commission.
    // Post-rollout orders carry the exact promise (djNetShare); legacy
    // orders approximate at the standard 85%.
    djPayoutsByCur[cur] =
      (djPayoutsByCur[cur] || 0) + (typeof o.djNetShare === "number" ? o.djNetShare : (o.amount || 0) * 0.85);
  }
  return {
    platformEarningsByCur,
    totalVolumeByCur,
    djPayoutsByCur,
    paidCount: inside.length,
    undatedPaidCount: undated.length,
  };
}

// Aggregations for the selected window. Top Talent + its money reflect REAL
// revenue only (paid & not refunded — see countsAsRevenue); the order list,
// the status mix, and the "selected" count keep every order so unpaid
// selections stay visible as intent. Same window as the KPIs above so the tab
// reads consistently. Filtered by createdAt — always present (Mongoose
// timestamps), so nothing silently drops out.
export function overviewBreakdown(
  orders: OverviewOrder[],
  range: TimeRangeKey,
  resolveDj: ReturnType<typeof overviewDjResolver>,
  now?: Date
) {
  const { inside } = partitionByRange(orders, range, (o) => o.createdAt, now);
  const recent = inside
    .slice()
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  const djMap: Record<string, { name: string; avatar?: string; amount: number; byCur: Record<string, number>; count: number; id: string }> = {};
  // An unpaid "pending" order is a song the fan selected but never paid for,
  // so it's counted under "selected" rather than "pending".
  const stMix: Record<string, number> = { selected: 0, pending: 0, queued: 0, accepted: 0, declined: 0, played: 0 };

  for (const o of recent) {
    // Status mix (intent) — counts EVERY order, with unpaid pending split out
    // as "selected".
    if (o.status === "pending") {
      if (o.paid) stMix.pending++;
      else stMix.selected++;
    } else if (o.status && o.status in stMix) {
      stMix[o.status]++;
    }

    // Top Talent leaderboard — REAL money only (paid & not refunded). Unpaid
    // selections never inflate the revenue ranking.
    if (!countsAsRevenue(o)) continue;
    const cur = (o.currency || "usd").toLowerCase();
    const { name, avatar, id } = resolveDj(o.dj);
    if (!djMap[id]) djMap[id] = { name, avatar, amount: 0, byCur: {}, count: 0, id };
    // `amount` (currency-blended) drives only the ranking + bar width; the
    // displayed figure uses `byCur` so currencies are shown side by side.
    djMap[id].amount += o.amount || 0;
    djMap[id].byCur[cur] = (djMap[id].byCur[cur] || 0) + (o.amount || 0);
    djMap[id].count += 1;
  }

  const sorted = Object.values(djMap).sort((a, b) => b.amount - a.amount).slice(0, 5);
  return { ordersInRange: recent, topDjs: sorted, statusMix: stMix, selectedCount: stMix.selected };
}

/* ---------- Revenue (AdminRevenueTab) ---------- */

// Synthetic id for the revenue bucket that collects orders whose talent was
// deleted (their populated `dj` is null). Groups them under one
// non-clickable "Unknown" row instead of dereferencing null.
export const UNKNOWN_DJ_ID = "__unknown__";

// Revenue tab aggregations — scoped to the selected time window. Filtering is
// by createdAt (always present); undated paid orders are excluded from a
// bounded window and only counted under All Time (surfaced via UndatedNote),
// so money is never silently dropped. Currencies stay split throughout.
export function revenueStats(orders: Order[], users: User[], range: TimeRangeKey, now?: Date) {
  const paidAll = orders.filter(countsAsRevenue);
  const { inside: paid, undated } = partitionByRange(paidAll, range, (o) => o.createdAt, now);
  const byCurrency: Record<string, { volume: number; fees: number; djShare: number; count: number }> = {};
  for (const o of paid) {
    const cur = (o.currency || "usd").toUpperCase();
    if (!byCurrency[cur]) byCurrency[cur] = { volume: 0, fees: 0, djShare: 0, count: 0 };
    const total = typeof o.totalAmount === "number" && o.totalAmount > 0 ? o.totalAmount : o.amount / 0.85;
    const fee = commissionOf(o);
    byCurrency[cur].volume += total;
    byCurrency[cur].fees += fee;
    // What the talent actually RECEIVES (their net share) — not their base
    // price. Server stores the exact promise on post-rollout orders
    // (djNetShare); legacy orders approximate at the standard 85%.
    byCurrency[cur].djShare += typeof o.djNetShare === "number" ? o.djNetShare : (o.amount || 0) * 0.85;
    byCurrency[cur].count++;
  }

  // Top 10 DJs by all-time revenue. A talent who was deleted leaves orders
  // whose populated `dj` is null — collapse those into one "Unknown" bucket
  // instead of crashing on a null dereference.
  const djTotals: Record<string, { name: string; avatar?: string; amount: number; byCur: Record<string, number>; feeByCur: Record<string, number>; countByCur: Record<string, number>; count: number }> = {};
  for (const o of paid) {
    let id: string;
    let name: string;
    let avatar: string | undefined;
    if (o.dj && typeof o.dj === "object") {
      id = o.dj._id;
      name = o.dj.name;
      avatar = o.dj.avatarUrl;
    } else if (typeof o.dj === "string") {
      id = o.dj;
      const u = users.find((x) => x._id === o.dj);
      name = u?.name ?? "Unknown";
      avatar = u?.avatarUrl;
    } else {
      id = UNKNOWN_DJ_ID;
      name = "Unknown";
      avatar = undefined;
    }
    const cur = (o.currency || "usd").toLowerCase();
    if (!djTotals[id]) djTotals[id] = { name, avatar, amount: 0, byCur: {}, feeByCur: {}, countByCur: {}, count: 0 };
    // `amount` (blended) drives only the leaderboard ranking; `byCur`/
    // `feeByCur`/`countByCur` drive the displayed revenue/fee/avg, split
    // by currency. The fee is the order's REAL stamped commission
    // (amount − djNetShare: standard, admin-override, or loyalty-tier rate
    // alike); legacy pre-stamp orders approximate at the standard 15%.
    const commission =
      (o.amount || 0) - (typeof o.djNetShare === "number" ? o.djNetShare : (o.amount || 0) * 0.85);
    djTotals[id].amount += o.amount;
    djTotals[id].byCur[cur] = (djTotals[id].byCur[cur] || 0) + o.amount;
    djTotals[id].feeByCur[cur] = (djTotals[id].feeByCur[cur] || 0) + commission;
    djTotals[id].countByCur[cur] = (djTotals[id].countByCur[cur] || 0) + 1;
    djTotals[id].count++;
  }
  const topDjs = Object.entries(djTotals)
    .sort(([, a], [, b]) => b.amount - a.amount)
    .slice(0, 10)
    .map(([id, v]) => ({ id, ...v }));

  return { byCurrency, topDjs, undatedPaidCount: undated.length };
}

/* ---------- Trending (AdminTrendingTab) ---------- */

export interface TrendingOrder {
  _id: string;
  searchQuery: string;
  amount: number;
  currency?: string;
  trackImage?: string;
  trackArtists?: string[];
  paid?: boolean;
  createdAt: string;
}

export type SongSort = "total" | "paid" | "selected" | "revenue";
export type ArtistSort = "total" | "paid" | "selected" | "revenue";
export type TrendingRangeKey = "1W" | "1M" | "3M" | "1Y" | "all";

// Rolling cutoff: createdAt >= now - range (null = no cutoff).
export function rangeCutoff(range: TrendingRangeKey, now: Date = new Date()): Date | null {
  if (range === "all") return null;
  const d = new Date(now);
  if (range === "1W") d.setDate(d.getDate() - 7);
  else if (range === "1M") d.setMonth(d.getMonth() - 1);
  else if (range === "3M") d.setMonth(d.getMonth() - 3);
  else d.setFullYear(d.getFullYear() - 1);
  return d;
}

// Filter orders by rolling range
export function trendingOrdersInRange(orders: TrendingOrder[], range: TrendingRangeKey, now?: Date) {
  const cutoff = rangeCutoff(range, now);
  if (!cutoff) return orders;
  return orders.filter((o) => new Date(o.createdAt) >= cutoff);
}

// Song aggregation
export function trendingSongs(filteredOrders: TrendingOrder[], songSort: SongSort, songDir: "desc" | "asc") {
  const map = new Map<string, {
    query: string; artists: string[]; image: string | null;
    paidCount: number; selectedCount: number;
    // `revenue` (blended) is kept ONLY for sort ordering; `revenueByCur`
    // is what gets displayed, so currencies are never added together.
    revenue: number; revenueByCur: Record<string, number>;
  }>();

  for (const o of filteredOrders) {
    const key = o.searchQuery.trim().toLowerCase();
    if (!key) continue;
    const isPaid = !!o.paid;
    const cur = (o.currency || "usd").toLowerCase();
    const existing = map.get(key);
    if (existing) {
      if (isPaid) {
        existing.paidCount++;
        existing.revenue += o.amount || 0;
        existing.revenueByCur[cur] = (existing.revenueByCur[cur] || 0) + (o.amount || 0);
      } else { existing.selectedCount++; }
      if (!existing.image && o.trackImage) existing.image = o.trackImage;
    } else {
      map.set(key, {
        query: o.searchQuery, artists: o.trackArtists || [], image: o.trackImage || null,
        paidCount: isPaid ? 1 : 0, selectedCount: isPaid ? 0 : 1,
        revenue: isPaid ? (o.amount || 0) : 0,
        revenueByCur: isPaid ? { [cur]: o.amount || 0 } : {},
      });
    }
  }

  const list = Array.from(map.values()).filter((s) => s.paidCount + s.selectedCount > 2);
  list.sort((a, b) => {
    let cmp = 0;
    if (songSort === "total") cmp = (a.paidCount + a.selectedCount) - (b.paidCount + b.selectedCount);
    else if (songSort === "paid") cmp = a.paidCount - b.paidCount;
    else if (songSort === "selected") cmp = a.selectedCount - b.selectedCount;
    else if (songSort === "revenue") cmp = a.revenue - b.revenue;
    return songDir === "desc" ? -cmp : cmp;
  });
  return list.slice(0, 20);
}

// Artist aggregation
export function topArtists(filteredOrders: TrendingOrder[], artistSort: ArtistSort, artistDir: "desc" | "asc") {
  const map = new Map<string, { name: string; paidCount: number; selectedCount: number; revenue: number; revenueByCur: Record<string, number> }>();

  for (const o of filteredOrders) {
    if (!o.trackArtists) continue;
    const isPaid = !!o.paid;
    const cur = (o.currency || "usd").toLowerCase();
    for (const artist of o.trackArtists) {
      const key = artist.trim().toLowerCase();
      if (!key) continue;
      const existing = map.get(key);
      if (existing) {
        if (isPaid) {
          existing.paidCount++;
          existing.revenue += o.amount || 0;
          existing.revenueByCur[cur] = (existing.revenueByCur[cur] || 0) + (o.amount || 0);
        } else { existing.selectedCount++; }
      } else {
        map.set(key, { name: artist, paidCount: isPaid ? 1 : 0, selectedCount: isPaid ? 0 : 1, revenue: isPaid ? (o.amount || 0) : 0, revenueByCur: isPaid ? { [cur]: o.amount || 0 } : {} });
      }
    }
  }

  const list = Array.from(map.values());
  list.sort((a, b) => {
    let cmp = 0;
    if (artistSort === "total") cmp = (a.paidCount + a.selectedCount) - (b.paidCount + b.selectedCount);
    else if (artistSort === "paid") cmp = a.paidCount - b.paidCount;
    else if (artistSort === "selected") cmp = a.selectedCount - b.selectedCount;
    else if (artistSort === "revenue") cmp = a.revenue - b.revenue;
    return artistDir === "desc" ? -cmp : cmp;
  });
  return list.slice(0, 20);
}

/* ---------- Requests (AdminOrdersTab) ---------- */

// The row the Requests table draws (now served by GET /admin/requests).
export type RequestOrder = AdminRequestRow;

export interface RequestFilters {
  filterStatus: string;
  filterDj: string;
  sortKey: "date" | "amount";
  searchQuery: string;
  // The From / To day bounds (requestDayBounds).
  from: Date | null;
  to: Date | null;
  filterMissingTrackHistory: boolean;
}

// The From / To date inputs → the first and last instant of those days, in
// the admin's own time zone.
// A date input's "YYYY-MM-DD" is a day on the admin's own calendar. new Date()
// reads that string as UTC midnight, which west of UTC is still the day
// before — so From Oct 9 in Los Angeles used to start on Oct 8. Build the
// local day from its parts instead.
function localDay(ymd: string, endOfDay: boolean): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (endOfDay) d.setHours(23, 59, 59, 999);
  return d;
}

export function requestDayBounds(dateFrom: string, dateTo: string): { from: Date | null; to: Date | null } {
  return {
    from: dateFrom ? localDay(dateFrom, false) : null,
    to: dateTo ? localDay(dateTo, true) : null,
  };
}

export function filterRequests(orders: RequestOrder[], { filterStatus, filterDj, sortKey, searchQuery, from, to, filterMissingTrackHistory }: RequestFilters) {
  let res = orders.slice();
  // "selected" = songs the fan picked but never paid for (unpaid pending);
  // "pending" = a PAID request still awaiting the DJ. Every other value
  // filters by the raw status.
  if (filterStatus === "selected") res = res.filter(isUnpaidSelection);
  else if (filterStatus === "pending") res = res.filter((o) => o.status === "pending" && !!o.paid);
  else if (filterStatus !== "all") res = res.filter((o) => o.status === filterStatus);
  if (filterDj !== "all") {
    res = res.filter((o) => {
      const id = typeof o.dj === "string" ? o.dj : o.dj?._id;
      return id === filterDj;
    });
  }
  if (searchQuery.trim()) {
    const q = searchQuery.trim().toLowerCase();
    res = res.filter((o) =>
      (o.customerEmail || "").toLowerCase().includes(q) ||
      (o.customerName || "").toLowerCase().includes(q) ||
      (o.clientNickname || "").toLowerCase().includes(q) ||
      (o.clientInstagram || "").toLowerCase().includes(q) ||
      (o.searchQuery || "").toLowerCase().includes(q)
    );
  }
  if (from) {
    const start = from;
    res = res.filter((o) => new Date(o.createdAt) >= start);
  }
  if (to) {
    const end = to;
    res = res.filter((o) => new Date(o.createdAt) <= end);
  }
  if (filterMissingTrackHistory) {
    // "Without track history" — narrows to played, paid orders missing
    // the file. Limited to status=played because that's the only state
    // the server-side endpoint accepts an upload for.
    res = res.filter((o) => o.status === "played" && !o.trackHistoryStripeFileId);
  }
  res.sort((a, b) =>
    sortKey === "amount" ? b.amount - a.amount : new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
  return res;
}

export function requestStats(filtered: RequestOrder[]) {
  // Money figures count REAL revenue only (paid & not refunded). Unpaid
  // selections and refunded/declined orders never inflate these totals.
  const paid = filtered.filter(countsAsRevenue);
  // Group every money figure by the order's own currency — never blend
  // USD + EUR (+ future currencies) into one total. Each map is
  // { currency: amount } and rendered side by side, e.g. "$190.39 | €0.30".
  const totalRevByCur: Record<string, number> = {};
  const platformFeesByCur: Record<string, number> = {};
  const paidCountByCur: Record<string, number> = {};
  for (const o of paid) {
    const cur = (o.currency || "usd").toLowerCase();
    const rev =
      typeof o.totalAmount === "number" && o.totalAmount > 0 ? o.totalAmount : o.amount / 0.85;
    const fee = commissionOf(o);
    totalRevByCur[cur] = (totalRevByCur[cur] || 0) + rev;
    platformFeesByCur[cur] = (platformFeesByCur[cur] || 0) + fee;
    paidCountByCur[cur] = (paidCountByCur[cur] || 0) + 1;
  }
  // Average request value is itself per-currency (mean of that currency's
  // orders) — averaging across currencies would be meaningless.
  const avgByCur: Record<string, number> = {};
  for (const cur of Object.keys(totalRevByCur)) {
    avgByCur[cur] = paidCountByCur[cur] > 0 ? totalRevByCur[cur] / paidCountByCur[cur] : 0;
  }
  const count = filtered.length;
  const completed = filtered.filter((o) => o.status === "played" || o.status === "accepted").length;
  return {
    totalRevByCur,
    platformFeesByCur,
    avgByCur,
    count,
    completionPct: count > 0 ? ((completed / count) * 100).toFixed(0) : "0",
  };
}
